import { debounce, isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { twJoin, twMerge } from 'tailwind-merge';

import SaveCancelGroup from '@/components/ui/custom/buttons/SaveCancelGroup';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Controls from '@/components/ui/custom/Controls';
import ImageUpload from '@/components/ui/custom/input/ImageUpload';
import { ArrowUturnLeftIcon } from '@/components/icons';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { bodyText, card, heading3 } from '@/styles/styles';
import { ROUTES } from '@/utils/routes';
import alert from '@/utils/shared/alert';
import { GenerateBlocksAction } from './actions/GenerateMatrix';
import { CourseStudioFormFields } from './CourseStudio.hooks';
import CourseStudioSteps from './CourseStudioSteps';
import CourseStudioDetailsStep from './details/CourseStudioDetailsStep';
import {
  StepInformation,
  StudioStep,
  useCourseStudioSteps,
} from './info/CourseStudioInfo.hooks';
import CourseStudioInfoStep from './info/CourseStudioInfoStep';
import CourseStudioMatrix from './matrix/CourseStudioMatrix';
import LessonBlocksMatrixStep from './matrix/LessonBlocksMatrixStep';
import CourseStudioObjectives from './objectives/CourseStudioObjectives';
import CourseStudioObjectivesStep from './objectives/CourseStudioObjectivesStep';
import { displayCourseInformation } from '@/utils/coursePath';
import { GenerateObjectivesAction } from './actions/GenerateObjectives';

type Props = {
  onSubmit: (data: CourseStudioFormFields) => Promise<CourseStudioFormFields>;
  loading?: boolean;
  studioPermissions: StudioPermissions;
  defaultValues?: CourseStudioFormFields;
};

export default function CourseStudioForm(props: Props) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { onSubmit, loading, studioPermissions, defaultValues } = props;

  const [submitting, setSubmitting] = useState(false);

  const form = useFormContext<CourseStudioFormFields>();

  const {
    watch,
    trigger,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = form;

  const stepsComponents: StepInformation[] = [
    {
      title: t('courseInformation'),
      component: (
        <CourseStudioInfoStep disabled={!studioPermissions.allowFirstStep} />
      ),
    },
    {
      title: t('courseObjectives'),
      component: (
        <CourseStudioObjectivesStep studioPermissions={studioPermissions} />
      ),
      actions: [
        <GenerateObjectivesAction
          key="generateObjectives"
          setValue={setValue}
          courseId={defaultValues?.course?.id}
          disabled={loading}
          hasValue={!!watch('objectives')?.length}
          locked={!defaultValues?.coursePath?.slug}
        />,
      ],
    },

    {
      title: t('curriculumMatrix'),
      component: (
        <LessonBlocksMatrixStep
          studioPermissions={studioPermissions}
          slug={defaultValues?.coursePath?.slug}
        />
      ),
      actions: [
        <GenerateBlocksAction
          key="generateBlocks"
          setValue={setValue}
          courseId={defaultValues?.coursePath?.id}
          disabled={loading}
          locked={!defaultValues?.objectives?.length}
          hasValue={
            !!watch('lessonBlocks')?.length || !!watch('directLessons')?.length
          }
        />,
      ],
    },
    ...(!watch('directLessons')?.length
      ? [
          {
            title: t('matrixDetails'),
            component: (
              <CourseStudioDetailsStep
                studioPermissions={studioPermissions}
                coursePathId={defaultValues?.coursePath?.id}
                loading={loading}
              />
            ),
          },
        ]
      : []),
  ];

  const { steps, step, setStep, studioStepsPaths } = useCourseStudioSteps({
    count: stepsComponents.length,
    onClick: handleStepStudio,
  });

  const { studioStep = studioStepsPaths[step] } = useParams();

  const navigate = useNavigate();

  const [changed, setChanged] = useState(false);

  const className = 'flex flex-col gap-2.5';

  const debouncedUpdate = useMemo(
    () =>
      debounce(async () => {
        await trigger();

        setChanged(true);
      }, 500),
    [trigger],
  );

  function navigateToStep(slug: string, step: number) {
    navigate(ROUTES.COURSES.STUDIO.STEP(slug, studioStepsPaths[step]));
  }
  // TODO: Em um hook (useAutoSave), definir cada condicional do step como uma função com o retorno booleano, cadastrar cada função em array, e depois aplicar um arr.some(fun => fun()), se uma das funções for true, o autosave será disparado. Isso resolve o OCP do SOLID.
  const shouldSave = (oldStep: number, newStep: number) => {
    let saveStep1to2 = false;
    saveStep1to2 = oldStep === 0 && newStep === 1;
    saveStep1to2 = saveStep1to2 && !defaultValues?.coursePath?.id;

    const watchObjectives = watch('objectives');
    const saveStep2to3 =
      oldStep === 1 &&
      newStep === 2 &&
      !isEqual(watchObjectives, defaultValues?.objectives);

    const watchLessonBlocks = watch('lessonBlocks');
    const saveStep3to4 =
      oldStep === 2 &&
      newStep === 3 &&
      !isEqual(watchLessonBlocks, defaultValues?.lessonBlocks);

    return saveStep1to2 || saveStep2to3 || saveStep3to4;
  };

  const autoSave = async (newStep: number) => {
    if (!shouldSave(step, newStep)) return;
    const course = await save(form.getValues());
    if (course?.coursePath?.slug) {
      navigateToStep(course.coursePath.slug, newStep);
    }
  };

  async function handleStepChange(direction: number) {
    const newStep = step + direction;
    setStep(newStep);
    autoSave(newStep);
    if (defaultValues?.coursePath?.slug) {
      navigateToStep(defaultValues.coursePath.slug, newStep);
    }
  }

  function handleStepStudio(stepIndex: number) {
    setStep(stepIndex);
    autoSave(stepIndex);
    if (defaultValues?.coursePath?.slug) {
      navigateToStep(defaultValues.coursePath.slug, stepIndex);
    }
  }

  async function onFinish(slug: string) {
    navigate(ROUTES.COURSES.BOX.INFO(slug));
    setSubmitting(false);
  }

  const directLessonsWithBlocksValidation = () => {
    const directLessons = watch('directLessons');
    const lessonBlocks = watch('lessonBlocks');

    return !!directLessons?.length && !!lessonBlocks?.length;
  };

  const lockActions =
    loading || !isValid || directLessonsWithBlocksValidation();

  const lessonQuantityValidation = () => {
    const lessonsQuantity = watch('coursePath.lessonsQuantity');
    const totalLessons =
      watch('lessonBlocks')?.reduce(
        (acc, block) => acc + (block.lessons?.length || 0),
        0,
      ) || 0;

    return {
      mismatch: lessonsQuantity && totalLessons > lessonsQuantity,
      lessonsQuantity,
      totalLessons,
    };
  };

  const save = async (data: CourseStudioFormFields) => {
    const { mismatch, lessonsQuantity, totalLessons } =
      lessonQuantityValidation();
    const directLessonsWithBlocksMismatch = directLessonsWithBlocksValidation();

    if (mismatch) {
      alert.error(
        t('lessonsQuantityMismatch', {
          expected: lessonsQuantity,
          actual: totalLessons,
        }),
      );
      return;
    }

    if (directLessonsWithBlocksMismatch) {
      alert.error(t('directLessonsWithBlocksMismatch'));
      return;
    }

    return await onSubmit(data);
  };

  const handleSave = async (data: CourseStudioFormFields) => {
    const saved = await save(data);

    if (!saved || !saved.coursePath) return;

    if (submitting) {
      onFinish(saved.coursePath.slug);
    } else {
      navigate(
        ROUTES.COURSES.STUDIO.STEP(
          saved.coursePath.slug,
          studioStep as StudioStep,
        ),
      );
    }
  };

  useEffect(() => {
    if (changed) {
      debouncedUpdate();
    }

    return () => {
      debouncedUpdate.cancel();
    };
  }, [changed, watch, debouncedUpdate, studioPermissions.allowFirstStep]);

  useEffect(() => {
    const subscribe = watch(value => {
      setChanged(!isEqual(value, defaultValues));
    });
    return () => {
      subscribe.unsubscribe();
    };
  }, [watch, defaultValues]);

  const courseInformation = displayCourseInformation(
    watch('course'),
    watch('coursePath.lessonsQuantity'),
  );

  const disabled = useMemo(
    () =>
      !studioPermissions.allowOtherSteps.simpleEditing &&
      !studioPermissions.allowOtherSteps.advancedEditing,
    [studioPermissions],
  );

  return (
    <form
      className="flex flex-col gap-2.5 h-full overflow-hidden"
      onSubmit={handleSubmit(handleSave)}
    >
      <article className="flex gap-2.5 min-h-[2.625rem] w-full justify-between shrink-0">
        <h3 className={twJoin(heading3, 'text-primary')}>
          {watch('course.name')}
        </h3>

        <SaveCancelGroup
          className="self-start justify-self-end flex-wrap gap-2.5"
          save={{
            type: 'submit',
            disabled: !isValid || !changed || disabled,
            loading,
            testId: 'saveButton',
          }}
          cancel={{
            icon: <ArrowUturnLeftIcon className="w-4 h-4" />,
            className: 'capitalize',
            text: t('revertActions'),
            disabled: loading || !changed || disabled,
            onClick: () => reset(),
            testId: 'cancelButton',
          }}
        />
      </article>

      <article className="grid grid-cols-1 lg:grid-cols-3 gap-2.5 flex-1 min-h-0">
        <div
          className={twJoin(
            className,
            card,
            'flex flex-col min-h-0 max-h-full',
          )}
        >
          <p className={twMerge(bodyText, 'text-neutral shrink-0')}>
            {t('fillInTheCourseInformation')}
          </p>
          <section className="flex gap-2.5 w-full shrink-0">
            <ConditionalRenderer condition={watch('course.bannerImg')}>
              <ImageUpload
                readonly
                value={watch('course.bannerImg')}
                className={{ div: 'w-[4rem]', base: 'w-fit' }}
              />
            </ConditionalRenderer>
            <p
              className={twMerge(
                bodyText,
                'text-neutral capitalize flex w-full self-center',
              )}
            >
              {courseInformation}
            </p>
          </section>

          <div className="flex flex-col gap-2.5 flex-1 min-h-0">
            <CourseStudioObjectives
              hasOpen={step === 1}
              objectives={watch('objectives')}
              className="flex-1 min-h-0"
            />
            <CourseStudioMatrix
              hasOpen={step === 2}
              blocks={watch('lessonBlocks')}
              directLessons={watch('directLessons')}
              className="flex-1 min-h-0"
            />
          </div>
        </div>
        <div className="col-span-2 flex flex-col gap-2.5 min-h-0">
          <CourseStudioSteps
            step={step}
            steps={steps}
            stepsComponents={stepsComponents}
            disabled={lockActions}
          />
          <section
            className={twJoin(
              className,
              card,
              'lg:col-span-2 flex flex-col gap-2.5 flex-1 justify-between overflow-hidden',
              step > 2 && 'border-none shadow-none',
            )}
          >
            {stepsComponents[step].component}
            <Controls
              submitting={submitting}
              disabled={lockActions}
              goNext={() => handleStepChange(1)}
              goBack={() => handleStepChange(-1)}
              isOnLast={step === steps.length - 1}
              hidePrev={step === 0}
              onFinish={() => setSubmitting(true)}
              isNextRequest={loading}
            />
          </section>
        </div>
      </article>
    </form>
  );
}
